import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ScrollText, ChevronDown, ChevronRight, RotateCcw, Filter, Search, X } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatDMYTime } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const ACTION_STYLES = {
  update: "bg-blue-100 text-blue-800",
  delete: "bg-rose-100 text-rose-800",
  restore: "bg-emerald-100 text-emerald-800",
  mark_sent: "bg-sky-100 text-sky-800",
  undo_sent: "bg-amber-100 text-amber-800",
  revision: "bg-violet-100 text-violet-800",
};

const MODULE_LABEL = {
  clients: "Clients",
  catalog: "Machine Catalog",
  inventory: "Inventory",
  leads: "Leads",
  quotations: "Quotations",
  bookings: "Demo Bookings",
  users: "Users",
  roles: "Roles & Permissions",
};

function formatVal(v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-400">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return <code className="text-[11px]">{JSON.stringify(v)}</code>;
  const s = String(v);
  return s.length > 120 ? <span title={s}>{s.slice(0, 120)}…</span> : s;
}

function ChangesTable({ changes }) {
  const entries = Object.entries(changes || {});
  if (!entries.length) return <div className="text-xs text-slate-500 italic">No field-level changes captured.</div>;
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-1.5 font-semibold w-36">Field</th>
            <th className="text-left px-3 py-1.5 font-semibold">Before</th>
            <th className="text-left px-3 py-1.5 font-semibold">After</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, { before, after }]) => (
            <tr key={field} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">{field}</td>
              <td className="px-3 py-1.5 text-rose-700 line-through decoration-rose-300">{formatVal(before)}</td>
              <td className="px-3 py-1.5 text-emerald-700">{formatVal(after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SnapshotBlock({ snapshot }) {
  const [open, setOpen] = useState(false);
  if (!snapshot) return null;
  return (
    <div className="mt-2">
      <button
        className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
        onClick={() => setOpen((v) => !v)}
        data-testid="audit-snapshot-toggle"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        View full snapshot
      </button>
      {open && (
        <pre className="mt-2 text-[11px] bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto max-h-64">
{JSON.stringify(snapshot, null, 2)}
        </pre>
      )}
    </div>
  );
}

function EntryRow({ entry, canRestore, onRestore }) {
  const [open, setOpen] = useState(false);
  const isDelete = entry.action === "delete";
  const changeCount = Object.keys(entry.changes || {}).length;

  return (
    <div className="border-b border-slate-100" data-testid={`audit-row-${entry.id}`}>
      <div
        className="grid grid-cols-12 gap-2 px-4 py-3 items-center cursor-pointer hover:bg-slate-50"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="col-span-2 text-xs text-slate-500 whitespace-nowrap">{formatDMYTime(entry.at)}</div>
        <div className="col-span-2 text-xs">
          <div className="font-medium text-slate-800 truncate">{entry.actor_name || "—"}</div>
          <div className="text-slate-500 truncate">{entry.actor_email}</div>
        </div>
        <div className="col-span-1">
          <Badge variant="outline" className="text-[10px] font-mono">{MODULE_LABEL[entry.module] || entry.module}</Badge>
        </div>
        <div className="col-span-1">
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${ACTION_STYLES[entry.action] || "bg-slate-100 text-slate-700"}`}>
            {entry.action}
          </span>
        </div>
        <div className="col-span-4 text-sm truncate" title={entry.target_label}>
          {entry.target_label || <span className="text-slate-400">—</span>}
          {entry.action !== "delete" && changeCount > 0 && (
            <span className="ml-2 text-xs text-slate-500">({changeCount} field{changeCount > 1 ? "s" : ""})</span>
          )}
        </div>
        <div className="col-span-2 flex justify-end gap-2">
          {isDelete && canRestore && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={(e) => { e.stopPropagation(); onRestore(entry); }}
              data-testid={`audit-restore-btn-${entry.id}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Restore
            </Button>
          )}
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 bg-slate-50/70">
          {changeCount > 0 && entry.action !== "delete" && <ChangesTable changes={entry.changes} />}
          {entry.extra && (
            <div className="mt-2 text-xs text-slate-700 bg-white border border-slate-200 rounded px-3 py-2 space-y-0.5" data-testid={`audit-extra-${entry.id}`}>
              {Object.entries(entry.extra).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                <div key={k}><span className="text-slate-500">{k.replace(/_/g, " ")}:</span> {String(v)}</div>
              ))}
            </div>
          )}
          {entry.snapshot && <SnapshotBlock snapshot={entry.snapshot} />}
          {entry.action === "restore" && (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded px-3 py-2">
              Record was restored from the archive by <b>{entry.actor_email}</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { actor_id: "all", module: "all", action: "all", date_from: "", date_to: "", q: "" };

const RESTORABLE_MODULES = new Set(["clients", "catalog", "inventory", "leads", "quotations"]);

export default function AuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [facets, setFacets] = useState({ modules: [], actions: [], actors: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/audit-log/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const load = async (nextPage = 1, nextFilters = filters) => {
    setLoading(true);
    try {
      const params = { page: nextPage, limit };
      if (nextFilters.actor_id && nextFilters.actor_id !== "all") params.actor_id = nextFilters.actor_id;
      if (nextFilters.module && nextFilters.module !== "all") params.module = nextFilters.module;
      if (nextFilters.action && nextFilters.action !== "all") params.action = nextFilters.action;
      if (nextFilters.date_from) params.date_from = nextFilters.date_from;
      if (nextFilters.date_to) params.date_to = nextFilters.date_to;
      if (nextFilters.q) params.q = nextFilters.q;
      const { data } = await api.get("/admin/audit-log", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1, filters); /* initial */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => load(1, filters);
  const clearFilters = () => { setFilters(EMPTY_FILTERS); load(1, EMPTY_FILTERS); };

  const restore = async (entry) => {
    if (!RESTORABLE_MODULES.has(entry.module)) {
      toast.error(`${MODULE_LABEL[entry.module] || entry.module} does not support restore`);
      return;
    }
    if (!window.confirm(`Restore "${entry.target_label}" from the archive?`)) return;
    try {
      await api.post(`/admin/restore/${entry.module}/${entry.target_id}`);
      toast.success("Record restored");
      load(page, filters);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const summary = useMemo(() => {
    const counts = { update: 0, delete: 0, restore: 0 };
    items.forEach((i) => { if (counts[i.action] !== undefined) counts[i.action] += 1; });
    return counts;
  }, [items]);

  if (user?.role !== "admin") {
    return <div className="p-6 text-slate-500">Admins only.</div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
          <ScrollText className="w-3.5 h-3.5" /> Governance
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Audit Log</h1>
        <p className="text-slate-500 text-sm mt-1">
          A tamper-evident trail of deletions, admin edits and quotation send, undo and revision actions across the ERP. Restore soft-deleted records with one click.
        </p>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-[11px] text-slate-500">User</Label>
            <Select value={filters.actor_id} onValueChange={(v) => setFilters({ ...filters, actor_id: v })}>
              <SelectTrigger data-testid="audit-filter-user"><SelectValue placeholder="Any user" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any user</SelectItem>
                {facets.actors.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Module</Label>
            <Select value={filters.module} onValueChange={(v) => setFilters({ ...filters, module: v })}>
              <SelectTrigger data-testid="audit-filter-module"><SelectValue placeholder="Any module" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any module</SelectItem>
                {facets.modules.map((m) => (
                  <SelectItem key={m} value={m}>{MODULE_LABEL[m] || m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Action</Label>
            <Select value={filters.action} onValueChange={(v) => setFilters({ ...filters, action: v })}>
              <SelectTrigger data-testid="audit-filter-action"><SelectValue placeholder="Any action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any action</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="restore">Restore</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="audit-filter-from" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="audit-filter-to" />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Search</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Target / email…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                data-testid="audit-filter-search"
              />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button onClick={applyFilters} disabled={loading} data-testid="audit-apply-filters" className="bg-indigo-600 hover:bg-indigo-700">
            Apply filters
          </Button>
          <Button variant="outline" onClick={clearFilters} data-testid="audit-clear-filters">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
          <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
            <span><b>{total}</b> entries</span>
            <span>·</span>
            <span className="text-blue-700">{summary.update} updates</span>
            <span className="text-rose-700">{summary.delete} deletes</span>
            <span className="text-emerald-700">{summary.restore} restores</span>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
          <div className="col-span-2">When</div>
          <div className="col-span-2">Who</div>
          <div className="col-span-1">Module</div>
          <div className="col-span-1">Action</div>
          <div className="col-span-4">Target</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">No audit entries match the current filters.</div>
        )}
        {items.map((entry) => (
          <EntryRow key={entry.id} entry={entry} canRestore={RESTORABLE_MODULES.has(entry.module)} onRestore={restore} />
        ))}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1, filters)} data-testid="audit-prev-page">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => load(page + 1, filters)} data-testid="audit-next-page">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
